import { cn } from "@apex/shared";
import { useWidget } from "../../state";
import { Button } from "../ui/Button";
import { ApiError } from "../../api";

export interface ErrorViewProps {
  className?: string;
}

export function ErrorView({ className }: ErrorViewProps) {
  const { state, goBack, reset } = useWidget();
  const { error, view } = state;

  // Determine error type and appropriate actions
  const isApiError = error instanceof ApiError;
  const isNetworkError = error?.message?.includes("fetch") || error?.message?.includes("network");
  // The browser can't surface a CORS rejection's reason; what we see is a
  // bare `TypeError: Failed to fetch` for every request despite our bundle
  // having loaded fine. Treat that as "probably disallowed origin" and
  // surface a more useful hint (B14). Same heuristic catches the
  // timeout-aborted variant raised by the client (B16).
  const isLikelyCorsOrUnreachable =
    !isApiError && (error?.message?.includes("Failed to fetch") ?? false);
  const isSubmissionError = view === "error" && state.quoteResult?.success === false;

  // Get user-friendly error message
  const getErrorMessage = (): string => {
    if (isSubmissionError) {
      // The raw 500-on-submit message ("API request failed: 500 Internal
      // Server Error") leaks protocol detail and reads as scary to a
      // customer. Replace with a friendly action-oriented copy; the
      // diagnostic raw text is logged to console.error from submitQuote.
      const raw = state.quoteResult?.message ?? "";
      if (/^API request failed:/i.test(raw)) {
        return "We couldn't submit your quote. Please try again — if this keeps happening, contact us.";
      }
      return raw || "Quote submission failed. Please try again.";
    }

    if (isApiError) {
      if (error.isUnauthorized) {
        return "Authentication failed. Please check your API key.";
      }
      if (error.isForbidden) {
        return "Access denied. You don't have permission to perform this action.";
      }
      if (error.isNotFound) {
        return "The requested resource was not found.";
      }
      if (error.isServerError) {
        return "A server error occurred. Please try again later.";
      }
    }

    if (isLikelyCorsOrUnreachable) {
      return "We can't reach the Apex API from this domain. If you've embedded this widget on a new domain, confirm it's in the API key's allowed-origins list.";
    }

    if (isNetworkError) {
      return "Unable to connect to the server. Please check your internet connection.";
    }

    return error?.message || "An unexpected error occurred. Please try again.";
  };

  // Get error title
  const getErrorTitle = (): string => {
    if (isSubmissionError) {
      return "Quote Submission Failed";
    }
    if (isLikelyCorsOrUnreachable) {
      return "Connection Error";
    }
    if (isNetworkError) {
      return "Connection Error";
    }
    if (isApiError && error.isUnauthorized) {
      return "Authentication Error";
    }
    return "Something Went Wrong";
  };

  return (
    <div
      className={cn(
        "apex-flex apex-flex-col apex-items-center apex-justify-center apex-min-h-[400px] apex-p-8 apex-text-center",
        className
      )}
    >
      {/* Error Icon */}
      <div className="apex-w-16 apex-h-16 apex-rounded-full apex-bg-error-light apex-flex apex-items-center apex-justify-center apex-mb-6">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="apex-text-error"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" x2="12" y1="8" y2="12" />
          <line x1="12" x2="12.01" y1="16" y2="16" />
        </svg>
      </div>

      {/* Title */}
      <h2 className="apex-text-2xl apex-font-bold apex-text-foreground apex-mb-2">
        {getErrorTitle()}
      </h2>

      {/* Message */}
      <p className="apex-text-muted-foreground apex-mb-6 apex-max-w-md">
        {getErrorMessage()}
      </p>

      {/* Technical Details (collapsed by default, dev only) */}
      {error && import.meta.env.DEV && (
        <details className="apex-w-full apex-max-w-md apex-mb-6 apex-text-left">
          <summary className="apex-text-sm apex-text-muted-foreground apex-cursor-pointer hover:apex-text-foreground">
            Technical details
          </summary>
          <pre className="apex-mt-2 apex-p-3 apex-bg-muted apex-rounded-md apex-text-xs apex-overflow-auto apex-max-h-32">
            {isApiError
              ? `Status: ${error.status}\n${error.body}`
              : error.stack || error.message}
          </pre>
        </details>
      )}

      {/* Actions */}
      <div className="apex-flex apex-gap-3">
        {/* Go Back - for submission errors */}
        {isSubmissionError && (
          <Button variant="outline" onClick={goBack}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="apex-mr-2"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
            Go Back
          </Button>
        )}

        {/* Try Again / Start Over */}
        <Button onClick={isSubmissionError ? goBack : reset}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="apex-mr-2"
          >
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
            <path d="M21 3v5h-5" />
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
            <path d="M8 16H3v5" />
          </svg>
          {isSubmissionError ? "Try Again" : "Start Over"}
        </Button>
      </div>
    </div>
  );
}
